import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";
import {
  GetCustomerShipmentListQuery,
  Status,
} from "../../../graphql/generated";
import SmartLynksTable, {
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import TripTimeline from "../../trip-planning/TripTimeline/TripTimeline";
import { orderStatusColors } from "../../order/OrdersList/OrdersList";
import enumLabel from "../../../utils/labels/enumLabel";
import { formatMileage } from "../../../utils/labels/formatDistance";
import ShipmentTrackerContainer from "../../tracking/ShipmentTracker";
import { differenceInSeconds } from "date-fns";

import { useTranslation } from "react-i18next";
import FeatureGuard, { Feature } from "../../account/Access/FeatureGuard";
import { testIds } from "./CustomerOrdersList.testIds";

type CustomerShipmentListQuery =
  GetCustomerShipmentListQuery["customerShipments"]["data"][0];

type CustomerOrdersListProps = {
  query: QueryFn<
    CustomerShipmentListQuery,
    "customerShipments",
    unknown,
    {
      status: Status | null;
    }
  >;
  onRefetch?: () => void;
  readonly?: boolean;
  disableSearch?: boolean;
  disablePagination?: boolean;
  disableStatusTab?: boolean;
};

export enum CustomerOrderStatusTab {
  ALL,
  UNASSIGNED,
  ASSIGNED,
  IN_PROGRESS,
  PAST,
  CANCELLED,
}

const statusTabToCustomerOrderStatus: Partial<
  Record<CustomerOrderStatusTab, Status | null>
> = {
  [CustomerOrderStatusTab.IN_PROGRESS]: null,
  [CustomerOrderStatusTab.PAST]: Status.Complete,
};

export default function CustomerOrdersList({
  query,
  disableStatusTab,
}: CustomerOrdersListProps) {
  const [statusTab, setStatusTab] = useState<CustomerOrderStatusTab>(
    CustomerOrderStatusTab.IN_PROGRESS
  );

  const { t } = useTranslation(["orders"]);

  const [selectedShipment, setSelectedShipment] =
    useState<CustomerShipmentListQuery | null>(null);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <FeatureGuard feature={Feature.CommodityManagement}>
        <Stack
          sx={{
            alignSelf: "end",
            mb: 2,
            mt: 2,
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button data-testid={testIds.customerOrdersListCreateOrder}
            href="/customer-portal/orders/new"
            variant="contained"
            color="secondary"
            size="large"
            id="new-customer-order-button"
          >
            {t("orders:new", "New Order")}
          </Button>
        </Stack>
      </FeatureGuard>
      {disableStatusTab ? null : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 2,
          }}
        >
          <Tabs data-testid={testIds.customerOrdersListInProgressNavigation}
            value={statusTab}
            onChange={(_, value) => {
              setStatusTab(value);
            }}
            aria-label="basic tabs example"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab data-testid={testIds.customerOrdersListInProgressView}
              label={t("orders:filter.inProgress", "In Progress")}
              value={CustomerOrderStatusTab.IN_PROGRESS}
            />
            <Tab data-testid={testIds.customerOrdersListCompleted}
              label={t("orders:filter.complete", "Completed")}
              value={CustomerOrderStatusTab.PAST}
            />
          </Tabs>
        </Box>
      )}
      <Grid
        sx={{
          px: 2,
          height: "calc(100% - 75px)",
        }}
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          sm={selectedShipment ? 6 : 12}
          sx={{ height: "100%", overflow: "auto" }}
        >
          <SmartLynksTable
            query={query}
            additionalQueryVariables={{
              status: statusTabToCustomerOrderStatus[statusTab] || null,
            }}
            renderRow={(shipment) => (
              <Card
                key={shipment._id}
                sx={(theme) => ({
                  mb: 1,
                  mt: 1,
                  cursor: "pointer",
                  backgroundColor:
                    selectedShipment?._id === shipment._id
                      ? theme.palette.action.selected
                      : "",
                })}
                onClick={() => {
                  if (selectedShipment?._id === shipment._id) {
                    setSelectedShipment(null);
                    return;
                  }
                  setSelectedShipment(shipment);
                }}
              >
                <CardContent>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="h6">
                      {`${t("orders:order")}# ${shipment.shipmentNumber} ${
                        shipment.referenceNumbers?.length
                          ? `/ ${shipment.referenceNumbers?.join(",")}`
                          : ""
                      }`}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={3}>
                      {shipment?.route
                        ? formatMileage(shipment.route.routeDistance)
                        : null}
                      <Chip
                        color={orderStatusColors[shipment.status]}
                        label={enumLabel(shipment.status)}
                        sx={{
                          ml: 3,
                        }}
                      />
                    </Stack>
                  </Stack>
                  <TripTimeline
                    horizontal
                    trip={{
                      _id: shipment._id,
                      shipmentLocations: (shipment.route?.locations || []).map(
                        (location, index) => ({
                          ...location,
                          tripShipmentLocationId: `temp-loc-${index}`,
                          shipmentId: shipment._id,
                          shipmentIds: [shipment._id],
                          shipmentLocationIds: [location._id],
                          arrivalTime: location.timeWindows[0]?.fromDate,
                          serviceDuration: location.timeWindows[0]
                            ? differenceInSeconds(
                                new Date(location.timeWindows[0]?.toDate),
                                new Date(location.timeWindows[0]?.fromDate)
                              )
                            : 0,
                          setupDuration: 0,
                          waitingDuration: 0,
                        })
                      ),
                      polyline: "",
                      shipments: [shipment],
                      status: shipment.status,
                    }}
                    onShipmentEventLog={() => {}}
                  />
                </CardContent>
              </Card>
            )}
            dataKey="customerShipments"
            id="customerOrders-table"
          />
        </Grid>
        {selectedShipment ? (
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              height: "100%",
            }}
          >
            {selectedShipment?.tripId ? (
              <ShipmentTrackerContainer
                shipmentId={selectedShipment?._id}
                mapOnly
                useStandardMap
              />
            ) : (
              <Alert severity="info">
                {t("orders:alert.notAssigned", "Order not yet assigned")}
              </Alert>
            )}
          </Grid>
        ) : null}
      </Grid>
    </div>
  );
}
